import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useStripeBalance } from '@/hooks/useStripeBalance';
import { StripeStatusAlert } from './stripe/StripeStatusAlert';
import { StripeErrorAlert } from './stripe/StripeErrorAlert';
import { Organization } from '@/types/organizationTypes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Clock, AlertCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { useStripeConnect } from '@/hooks/stripe/useStripeConnect';
import { useStripeReturnFlow } from '@/hooks/stripe/useStripeReturnFlow';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface StripeConnectSettingsProps {
  organization: Organization | null;
  organizationId: string | undefined;
}

const StripeConnectSettings: React.FC<StripeConnectSettingsProps> = ({ 
  organization,
  organizationId 
}) => {
  // Check the Stripe balance to verify connection status
  const {
    data: balanceData,
    isError: balanceError,
    isLoading: balanceLoading
  } = useStripeBalance();

  // Get status from organization data
  const stripeAccountId = organization?.billing_settings?.stripe_account_id || null;

  // Use the composed Stripe Connect hook for status and actions
  const {
    loading: connectLoading,
    accountStatus,
    detailedStatus,
    errorMessage: connectError,
    createConnectAccount,
    disconnectStripeAccount
  } = useStripeConnect(organization, organizationId);

  // Handle return flow from Stripe onboarding
  const {
    isProcessing: returnProcessing,
    returnResult
  } = useStripeReturnFlow({
    organizationId,
    stripeAccountId,
    onStatusUpdate: (result) => {
      // Force refresh of balance data and account status if return was successful
      if (result.shouldRefresh) {
        // Small delay to allow Stripe's systems to propagate the changes
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    }
  });
  // Primary connection status based on account ID existence
  // If we have a Stripe account ID, consider it connected unless there's a specific error
  const isConnected = stripeAccountId && (!balanceError && !balanceData?.error);
  const hasError = balanceError || balanceData?.error || connectError;
  
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [showExistingAccountForm, setShowExistingAccountForm] = useState(false);
  const [existingAccountId, setExistingAccountId] = useState('');
  const [connectingExistingAccount, setConnectingExistingAccount] = useState(false);

  const handleContinueOnboarding = async () => {
    if (!organizationId) return;
    setOnboardingLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const response = await fetch('https://muoihzfomfercsujyyta.supabase.co/functions/v1/generate-stripeconnect-onboarding-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({ organizationId }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to generate onboarding link');
      }
    } catch (err) {
      alert('Could not generate onboarding link. Please try again.');
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleConnectExistingAccount = async () => {
    if (!organizationId || !existingAccountId.trim()) {
      toast.error('Please enter a valid Stripe Connect account ID');
      return;
    }

    // Basic validation - Stripe Connect account IDs start with 'acct_'
    if (!existingAccountId.startsWith('acct_')) {
      toast.error('Invalid Stripe Connect account ID. Account IDs should start with "acct_"');
      return;
    }

    setConnectingExistingAccount(true);
    
    try {
      // Update the organization with the existing Stripe account ID
      const newBillingSettings = {
        ...organization?.billing_settings,
        stripe_account_id: existingAccountId.trim()
      };
      
      const { error: updateError } = await supabase
        .from('organizations')
        .update({ 
          billing_settings: newBillingSettings
        })
        .eq('id', organizationId);
        
      if (updateError) throw updateError;
      
      toast.success('Existing Stripe Connect account connected successfully!', {
        description: 'Your account is now ready to receive payments through the platform.'
      });
      
      // Reset form
      setExistingAccountId('');
      setShowExistingAccountForm(false);
      
      // Refresh the page to update the UI
      window.location.reload();
      
    } catch (error) {
      console.error('Error connecting existing Stripe account:', error);
      toast.error('Failed to connect existing Stripe account', {
        description: error instanceof Error ? error.message : 'Please check your account ID and try again'
      });
    } finally {
      setConnectingExistingAccount(false);
    }
  };

  // Handle the case when organization data is completely missing
  if (!organization) {
    return (
      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-800" />
        <AlertTitle className="text-amber-800">Organization data not available</AlertTitle>
        <AlertDescription className="text-amber-700">
          Please refresh the page to load organization data.
        </AlertDescription>
      </Alert>
    );
  }

  // Handle loading state
  if (balanceLoading && !balanceData) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <h3 className="text-lg font-semibold">Stripe Connect Integration</h3>
          <p className="text-sm text-muted-foreground">Loading payment information...</p>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Stripe not connected warning banner */}
      {!stripeAccountId && (
        <Alert className="bg-yellow-50 border-yellow-200 flex items-center">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
          <div>
            <AlertTitle className="text-yellow-800">Online payment is not available</AlertTitle>
            <AlertDescription className="text-yellow-700">
              Parents will not be able to pay invoices online until you connect your Stripe account.
            </AlertDescription>
          </div>
        </Alert>
      )}
      <div className="flex flex-col space-y-2">
        <h3 className="text-lg font-semibold">Stripe Connect Integration</h3>
        <p className="text-sm text-muted-foreground">
          Connect your Stripe account to receive payments directly from parents and customers.
          <span className="block mt-1 font-medium">
            Note: This is separate from the platform subscription payments which go to Hivetivity.
          </span>
        </p>
      </div>

      {/* Return Flow Status - Show immediate feedback for users returning from Stripe */}
      {returnProcessing && (
        <Alert className="bg-blue-50 border-blue-200">
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
          <AlertTitle className="text-blue-800">Processing your return from Stripe...</AlertTitle>
          <AlertDescription className="text-blue-700">
            We're checking your account status and updating your payment settings.
          </AlertDescription>
        </Alert>
      )}
      
      {returnResult.status === 'success' && returnResult.message && (
        <Alert className="bg-green-50 border-green-200">
          <Clock className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Welcome back!</AlertTitle>
          <AlertDescription className="text-green-700">
            {returnResult.message}
            {returnResult.shouldRefresh && (
              <span className="block mt-2 text-sm">
                This page will refresh automatically in a moment to update your status.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {returnResult.status === 'error' && returnResult.message && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Onboarding not completed</AlertTitle>
          <AlertDescription className="text-amber-700">
            {returnResult.message}
            <span className="block mt-2 text-sm">
              Don't worry - you can continue the setup process using the button below.
            </span>
          </AlertDescription>
        </Alert>
      )}
      
      {hasError && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">
            {stripeAccountId ? "Connection Error" : "Configuration Error"}
          </AlertTitle>
          <AlertDescription className="text-red-700">
            {stripeAccountId 
              ? `Unable to verify your Stripe account status: ${balanceData?.error || connectError || "Please try refreshing the page."}`
              : "Payment system not configured. Please connect your Stripe account to accept payments."
            }
          </AlertDescription>
        </Alert>
      )}
      
      {isConnected && (
        <Alert className="bg-green-50 border-green-200">
          <Clock className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Stripe Connected</AlertTitle>
          <AlertDescription className="text-green-700">
            Your Stripe account is connected. You can now receive payments directly from customers.
            {balanceData?.isTestMode && (
              <span className="block mt-1 font-medium">
                Note: Your account is currently in test mode. No real payments will be processed.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Show Continue Onboarding if account exists but is not fully onboarded */}
      {stripeAccountId && detailedStatus?.requiresAction && (
        <div className="flex justify-end mt-2">
          <Button onClick={handleContinueOnboarding} disabled={onboardingLoading}>
            {onboardingLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue Stripe Onboarding
          </Button>
        </div>
      )}
      
      {!isConnected && !hasError && (
        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">No Payment System Connected</AlertTitle>
          <AlertDescription className="text-blue-700">
            Connect your Stripe account to start accepting payments from customers.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Existing Account Connection Form */}
      {!isConnected && showExistingAccountForm && (
        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Connect Existing Stripe Account</AlertTitle>
          <AlertDescription className="text-blue-700 space-y-4">
            <p>If you already have a Stripe Connect account, you can connect it directly by entering your account ID.</p>
            <div className="space-y-2">
              <Label htmlFor="stripe-account-id" className="text-sm font-medium">
                Stripe Connect Account ID
              </Label>
              <Input
                id="stripe-account-id"
                value={existingAccountId}
                onChange={(e) => setExistingAccountId(e.target.value)}
                placeholder="acct_xxxxxxxxxxxxxxxx"
                className="max-w-md"
              />
              <p className="text-xs text-blue-600">
                Your Stripe Connect account ID starts with "acct_" and can be found in your Stripe Dashboard.
                <a 
                  href="https://dashboard.stripe.com/settings/connect" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-1 underline inline-flex items-center"
                >
                  Find your account ID <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </p>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={handleConnectExistingAccount}
                disabled={connectingExistingAccount || !existingAccountId.trim()}
                size="sm"
              >
                {connectingExistingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect Account
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowExistingAccountForm(false)}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end space-x-2">
        {isConnected ? (
          <Button 
            variant="outline" 
            onClick={disconnectStripeAccount}
            disabled={balanceLoading || connectLoading}
          >
            {(balanceLoading || connectLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Disconnect Stripe Account
          </Button>
        ) : (
          <>
            {!showExistingAccountForm && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setShowExistingAccountForm(true)}
                  disabled={balanceLoading || connectLoading}
                >
                  Connect Existing Account
                </Button>
                <Button 
                  onClick={createConnectAccount}
                  disabled={balanceLoading || connectLoading}
                >
                  {(balanceLoading || connectLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create New Stripe Account
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StripeConnectSettings;
